import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { BASE_URL } from "../../Components/Constant";
import ApiService from "../../Components/apiService.js";
import { Spin } from "antd";

const Bar = ({ reportId }) => {
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupField, setGroupField] = useState("Unknown");
  const [activeKey, setActiveKey] = useState(null);
  const [aggregationField, setAggregationField] = useState(null);

  const getSafeValue = (val) => {
    if (!val) return "Unknown";
    if (typeof val === "object") return val?.Name || val?._id || "Unknown";
    return String(val);
  };

  useEffect(() => {
    if (!reportId) return;

    const api = new ApiService(
      `${BASE_URL}/mt_reports/${reportId}`,
      { "Content-Type": "application/json" },
      "GET"
    );

    api
      .makeCall()
      .then((response) => {
        if (!response.report || !response.data) return;

        const groupFields = response.report.group_fields || [];
        const mainGroup = groupFields[0];
        const subGroup = groupFields[1];
        const agg = response.report.aggregation_fields?.[0];

        setGroupField(mainGroup);
        setAggregationField(agg);

        // -------------------------
        // SINGLE GROUP
        // -------------------------
        if (groupFields.length === 1) {
          const grouped = {};
          const aggKey = agg || "Count";

          response.data.forEach((r) => {
            let g = getSafeValue(r[mainGroup]);

            if (!grouped[g]) grouped[g] = { [mainGroup]: g, [aggKey]: 0 };

            grouped[g][aggKey] += agg ? parseFloat(r[agg]) || 0 : 1;
          });

          setData(Object.values(grouped));
          setKeys([aggKey]);  // legend now shows aggregation field
          setLoading(false);
          return;
        }


        // -------------------------
        // TWO GROUPS
        // -------------------------
        const final = {};
        const subKeysSet = new Set();

        response.data.forEach((r) => {
          const mainV = getSafeValue(r[mainGroup]);
          const subV = getSafeValue(r[subGroup]);

          if (!final[mainV]) final[mainV] = { [mainGroup]: mainV };

          if (!final[mainV][subV]) final[mainV][subV] = 0;

          final[mainV][subV] += parseFloat(r[agg]) || 0;
          subKeysSet.add(subV);
        });

        setData(Object.values(final));
        setKeys([...subKeysSet]);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [reportId]);

  const pastelColors = [
    "#FFB6B9",
    "#FAE3D9",
    "#BBDED6",
    "#8AC6D1",
    "#FFD3B6",
    "#D5ECC2",
    "#FFC8A2",
    "#C9BBCF",
  ];

  if (loading) return <Spin size="large" />;
  if (error) return <div>Error: {error.message}</div>;


  const getMaxStackedValue = (data, keys) => {
    if (!data?.length || !keys?.length) return "auto";

    return Math.max(
      ...data.map((row) =>
        keys.reduce(
          (total, k) => total + (parseFloat(row[k]) || 0),
          0
        )
      )
    );
  };



  return (
    <ResponsiveBar
      data={data}
      keys={keys}
      indexBy={groupField}
      groupMode="stacked"
      margin={{ top: 30, right: 150, bottom: 120, left: 70 }}
      padding={0.3}
      borderRadius={6}
      maxValue={getMaxStackedValue(data, keys)}
      colors={({ id }) => {
        const i = keys.indexOf(id);
        const base = pastelColors[i % pastelColors.length];

        if (!activeKey) return base; // no legend selected → normal
        return activeKey === id ? base : "#e0e0e0"; // faded others
      }}

      axisBottom={{
        renderTick: (tick) => {
          const fullLabel = tick.value || "";
          const maxLen = 12;

          const shortLabel =
            fullLabel.length > maxLen
              ? fullLabel.substring(0, maxLen) + "…"
              : fullLabel;

          return (
            <g transform={`translate(${tick.x},${tick.y + 20})`}>
              <title>{fullLabel}</title>
              <text
                textAnchor="end"
                transform="rotate(-45)"
                style={{ fontSize: 12, cursor: "pointer" }}
              >
                {shortLabel}
              </text>
            </g>
          );
        },
      }}
      enableGridY={true}
      legends={[
        {
          dataFrom: "keys",
          anchor: "right",
          direction: "column",
          translateX: 120,
          itemWidth: 110,
          itemHeight: 18,
          symbolSize: 14,
          onClick: legend => {
            const clickedKey = legend.id;
            setActiveKey(prev => prev === clickedKey ? null : clickedKey);
          }
        }
      ]}

    />
  );
};

export default Bar;
